import { AnexosCard } from './anexos-card';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { api, ApiException } from '../../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Spinner } from '../../components/ui/spinner';
import { fmtBRL, fmtDate, fmtDateTime, fmtCompetencia } from '../../lib/format';
import { ArrowLeft, ArrowRight, Clock, FileText, RotateCcw } from 'lucide-react';
import {
  etapaLabel, etapaColor, statusFinLabel, statusFinColor,
  origemLabel, parcelaStatusLabel, parcelaStatusColor,
} from './labels';

interface Detalhe {
  id: string;
  numeroContrato: string;
  origemOperacional: string;
  valorTotal: number;
  mensalidadeAssociativa: number;
  qtdParcelas: number;
  comissaoPercentual: number;
  comissaoValor: number;
  etapaOperacional: string;
  statusFinanceiro: string;
  dataSolicitacao: string;
  dataInicioVigencia: string | null;
  dataLiquidacao: string | null;
  observacoes: string | null;
  transicoesPossiveis: string[];
  associado: { id: string; nome: string; documentoMask: string; matriculaServidor: string; orgaoPublico: string | null } | null;
  agente: { id: string; nome: string } | null;
  parcelas: Array<{
    id: string; numeroParcela: number; competencia: string;
    vencimento: string; valorPrevisto: number; valorPago: number;
    status: string; dataPagamento: string | null; origemBaixa: string | null;
  }>;
  statusHistorico: Array<{
    id: string; campo: string; valorDe: string | null; valorPara: string;
    motivoTexto: string | null; changedAt: string;
  }>;
}

export function ContratoDetalhePage() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const [motivo, setMotivo] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['contrato', id],
    queryFn: () => api<Detalhe>(`/contratos/${id}`),
    enabled: !!id,
  });

  const transitar = useMutation({
    mutationFn: (novaEtapa: string) =>
      api(`/contratos/${id}/transicao`, { method: 'POST', body: { novaEtapa, motivo: motivo || undefined } }),
    onSuccess: () => {
      setMotivo('');
      setActionError(null);
      qc.invalidateQueries({ queryKey: ['contrato', id] });
    },
    onError: (err: ApiException) => setActionError(err.apiError?.message ?? 'Falha na transição'),
  });

  const recalcular = useMutation({
    mutationFn: () => api(`/contratos/${id}/recalcular-status`, { method: 'POST' }),
    onSuccess: () => {
      setActionError(null);
      qc.invalidateQueries({ queryKey: ['contrato', id] });
    },
    onError: (err: ApiException) => setActionError(err.apiError?.message ?? 'Falha ao recalcular'),
  });

  if (isLoading) return <Spinner />;
  if (!data) return <p>Contrato não encontrado</p>;

  const totalPago = data.parcelas.reduce((s, p) => s + p.valorPago, 0);
  const pctPago = data.valorTotal > 0 ? (totalPago / data.valorTotal) * 100 : 0;

  return (
    <div className="space-y-6 max-w-5xl">
      <Link to="/contratos" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
        <ArrowLeft className="h-3 w-3" />voltar para contratos
      </Link>

      <div>
        <div className="flex items-center gap-3 flex-wrap mb-1">
          <h1 className="text-3xl font-semibold tracking-tight font-mono">{data.numeroContrato}</h1>
          <span className={`text-xs px-2 py-1 rounded ${etapaColor[data.etapaOperacional] ?? 'bg-gray-100'}`}>
            {etapaLabel[data.etapaOperacional] ?? data.etapaOperacional}
          </span>
          <span className={`text-xs px-2 py-1 rounded ${statusFinColor[data.statusFinanceiro] ?? 'bg-gray-100'}`}>
            {statusFinLabel[data.statusFinanceiro] ?? data.statusFinanceiro}
          </span>
          <span className="text-xs px-2 py-1 rounded bg-muted/70">{origemLabel[data.origemOperacional] ?? data.origemOperacional}</span>
        </div>
        {data.associado && (
          <p className="text-muted-foreground">
            <Link to={`/associados/${data.associado.id}`} className="hover:underline font-medium">
              {data.associado.nome}
            </Link>
            {' · '}<span className="font-mono">{data.associado.documentoMask}</span>
            {' · '}matrícula {data.associado.matriculaServidor}
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Valor total</CardDescription>
            <CardTitle className="text-2xl">{fmtBRL(data.valorTotal)}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Mensalidade</CardDescription>
            <CardTitle className="text-2xl">{fmtBRL(data.mensalidadeAssociativa)}</CardTitle>
            <p className="text-xs text-muted-foreground">{data.qtdParcelas}x</p>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Comissão</CardDescription>
            <CardTitle className="text-2xl">{fmtBRL(data.comissaoValor)}</CardTitle>
            <p className="text-xs text-muted-foreground">{data.comissaoPercentual}%</p>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Total pago</CardDescription>
            <CardTitle className="text-2xl">{fmtBRL(totalPago)}</CardTitle>
            <div className="mt-2 h-2 bg-muted rounded-full overflow-hidden">
              <div className="h-full bg-green-500 transition-all" style={{ width: `${Math.min(pctPago, 100)}%` }} />
            </div>
            <p className="text-xs text-muted-foreground mt-1">{pctPago.toFixed(1)}%</p>
          </CardHeader>
        </Card>
      </div>

      {data.transicoesPossiveis.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><ArrowRight className="h-4 w-4" />Próximas etapas</CardTitle>
            <CardDescription>Clique para avançar o contrato na esteira</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <input
                type="text"
                placeholder="Motivo (opcional)"
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
              <div className="flex gap-2 flex-wrap">
                {data.transicoesPossiveis.map((etapa) => (
                  <Button key={etapa} size="sm"
                    variant={etapa === 'cancelado' || etapa === 'devolvido' ? 'secondary' : 'primary'}
                    disabled={transitar.isPending}
                    onClick={() => transitar.mutate(etapa)}>
                    {etapaLabel[etapa] ?? etapa}
                  </Button>
                ))}
                <Button size="sm" variant="ghost" disabled={recalcular.isPending}
                  onClick={() => recalcular.mutate()}>
                  <RotateCcw className="h-3 w-3 mr-1" />Recalcular status financeiro
                </Button>
              </div>
              {actionError && (
                <div className="text-sm text-destructive">{actionError}</div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><FileText className="h-4 w-4" />Parcelas</CardTitle>
        </CardHeader>
        <CardContent>
          <table className="w-full text-sm">
            <thead className="text-xs text-muted-foreground border-b">
              <tr>
                <th className="text-left py-2 pr-3">#</th>
                <th className="text-left py-2 pr-3">Competência</th>
                <th className="text-left py-2 pr-3">Vencimento</th>
                <th className="text-right py-2 pr-3">Previsto</th>
                <th className="text-right py-2 pr-3">Pago</th>
                <th className="text-left py-2 pr-3">Status</th>
                <th className="text-left py-2">Origem</th>
              </tr>
            </thead>
            <tbody>
              {data.parcelas.map((p) => (
                <tr key={p.id} className="border-b last:border-0">
                  <td className="py-2 pr-3 text-muted-foreground">{p.numeroParcela}</td>
                  <td className="py-2 pr-3">{fmtCompetencia(p.competencia)}</td>
                  <td className="py-2 pr-3">{fmtDate(p.vencimento)}</td>
                  <td className="py-2 pr-3 text-right">{fmtBRL(p.valorPrevisto)}</td>
                  <td className="py-2 pr-3 text-right">{p.valorPago > 0 ? fmtBRL(p.valorPago) : '—'}</td>
                  <td className="py-2 pr-3">
                    <span className={`text-xs px-2 py-0.5 rounded ${parcelaStatusColor[p.status] ?? 'bg-gray-100'}`}>
                      {parcelaStatusLabel[p.status] ?? p.status}
                    </span>
                  </td>
                  <td className="py-2 text-xs text-muted-foreground">{p.origemBaixa ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><Clock className="h-4 w-4" />Linha do tempo</CardTitle>
          <CardDescription>{data.statusHistorico.length} eventos registrados</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {data.statusHistorico.map((h) => (
              <div key={h.id} className="flex gap-3 text-sm border-l-2 border-muted pl-3">
                <div className="flex-1">
                  <div className="font-medium">
                    {h.campo === 'etapa_operacional' ? 'Etapa' : 'Status financeiro'}:{' '}
                    {h.valorDe && <span className="text-muted-foreground">{h.valorDe} → </span>}
                    <span>{h.valorPara}</span>
                  </div>
                  {h.motivoTexto && <p className="text-xs text-muted-foreground">{h.motivoTexto}</p>}
                </div>
                <div className="text-xs text-muted-foreground whitespace-nowrap">{fmtDateTime(h.changedAt)}</div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <AnexosCard contratoId={data.id} />

      {data.observacoes && (
        <Card>
          <CardHeader><CardTitle className="text-base">Observações</CardTitle></CardHeader>
          <CardContent>
            <p className="text-sm whitespace-pre-wrap">{data.observacoes}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
